import styled, { css } from 'styled-components';
import React, { ReactElement, useEffect } from 'react';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import { useSelector } from 'react-redux';
import { caption1 } from '@westwing/ui-kit/typography';
import { StyledDismissIcon } from '@westwing/ui-kit/Icons/StyledIcons';
import memoizeOne from 'memoize-one';
import StateInterface from '../../../redux/types';
import { Banner } from '../../../redux/headerBarBanner/types';
import { HeaderBarBannerData } from '../../../../common/apiAdapters/types';
import HybridLink from '../../Generic/Links/HybridLink';
import useHeaderBarBanner, { usePreviousHeaderBarID } from '../../../hooks/flags/useHeaderBarBanner';
import useGa from '../../../hooks/useGa';
import { TrackHeaderBarBannerActions } from '../../../../common/ga/types';
import { BellaTestIds, VictorQaClasses } from '../../../../common/qaIds';
import { textLinesLimitEllipsisCss } from 'Client/components/Generic/Text/StyledText';
import { HeaderModeType, useLayoutByPage } from 'Client/hooks/useLayoutByPage';

const AN_HOUR_IN_SECONDS = 3600;

const StyledWrapper = styled.div<{ background: string; isMiniHeader: boolean }>`
    ${caption1}
    display: none;
    position: relative;
    width: 100%;
    overflow: hidden;
    justify-content: center;
    align-items: center;
    min-height: 34px;
    padding: 10px 30px;
    z-index: ${({ theme }) => theme.zindexHeaderBarBanner};
    background: ${({ background }) => background};

    ${({ isMiniHeader }) =>
        isMiniHeader
            ? css`
                  display: none;

                  ${forDesktop`${css`
                      display: flex;
                  `}`}
              `
            : css`
                  display: flex;
              `}
`;

const MobileText = styled.span<{ textColor: string }>`
    ${textLinesLimitEllipsisCss(2)}
    width: 100%;
    color: ${({ textColor }) => textColor};
    text-align: center;
    ${forDesktop`
        display: none;
    `}
`;

const DesktopText = styled.span<{ textColor: string }>`
    display: none;
    width: 100%;
    color: ${({ textColor }) => textColor};
    text-align: center;
    ${forDesktop`
        display: block;
    `}
`;

const BannerLink = styled(HybridLink)`
    width: 100%;
`;

export const StyledBannerDismissIcon = styled(StyledDismissIcon)<{ textColor: string }>`
    position: absolute;
    right: 0;
    bottom: 50%;
    transform: translateY(50%) scale(0.7, 0.7);
    width: 30px;
    height: 30px;
    background-image: radial-gradient(closest-side at 50% 50%, rgba(0, 0, 0, 0.1), transparent);
    padding: 0;
    cursor: pointer;

    &:before {
        top: 12px;
        border-top: 1px solid ${({ textColor }) => textColor};
    }

    &:after {
        top: -10px;
        border-top: 1px solid ${({ textColor }) => textColor};
    }
`;

export interface BannerProps {
    bannerData: HeaderBarBannerData;
}

const BannerText: React.FC<{ textColor: string; mobile: string; desktop: string }> = ({
    textColor,
    mobile,
    desktop,
}) => {
    const { headerMode } = useLayoutByPage();
    const isMiniHeader = headerMode === HeaderModeType.MINI;

    return (
        <>
            {!isMiniHeader && (
                <MobileText
                    data-testid="header-banner-text-mobile"
                    textColor={textColor}
                    dangerouslySetInnerHTML={{ __html: mobile || desktop }}
                />
            )}
            <DesktopText
                data-testid="header-banner-text-desktop"
                textColor={textColor}
                dangerouslySetInnerHTML={{ __html: desktop || mobile }}
            />
        </>
    );
};

export const generateBannerChangeIdImpl = (banner: Banner) => {
    return Object.keys(banner)
        .map(key => banner[key])
        .join('');
};

export const generateBannerChangeId = memoizeOne(generateBannerChangeIdImpl);

/**
 * new version of the header bar banner
 * which uses data from contentful via the api
 */
const HeaderBarBannerV2 = (): ReactElement => {
    const [previousHbbContent, setCurrentContent] = usePreviousHeaderBarID();
    const [isDismissed, dismissHeaderBarBanner, resetHeaderBannerCookie] = useHeaderBarBanner();
    const banner = useSelector<StateInterface, Banner | undefined>(state => state.headerBarBanner.banner);
    const ga = useGa();
    const { headerMode } = useLayoutByPage();

    const currentHddContent = banner ? generateBannerChangeId(banner) : undefined;
    const hasDismissedCurrentHbb = currentHddContent === previousHbbContent && isDismissed;

    useEffect(() => {
        if (!!currentHddContent && currentHddContent !== previousHbbContent) {
            setCurrentContent(currentHddContent);
            resetHeaderBannerCookie();
        }
    }, [currentHddContent, previousHbbContent, resetHeaderBannerCookie, setCurrentContent]);

    useEffect(() => {
        if (banner && !hasDismissedCurrentHbb) {
            ga.oneHeader.trackHeaderBarActions(TrackHeaderBarBannerActions.Show);
        }
    }, [banner, hasDismissedCurrentHbb, ga.oneHeader]);

    if (!banner || hasDismissedCurrentHbb) {
        return <></>;
    }

    const { desktopHTML, mobileHTML, fontColor, backgroundColor, link, dismissible, reappearenceDelay } = banner;

    const handleDismissIconClick = () => {
        ga.oneHeader.trackHeaderBarActions(TrackHeaderBarBannerActions.Dismiss);
        // converting hours to seconds and defaulting to 24 hours
        const delay = reappearenceDelay ? reappearenceDelay * AN_HOUR_IN_SECONDS : 24 * AN_HOUR_IN_SECONDS;
        dismissHeaderBarBanner(delay);
    };

    return (
        <StyledWrapper
            background={backgroundColor}
            data-testid={BellaTestIds.headerBarBanner}
            className={VictorQaClasses.headerPromotionBanner}
            isMiniHeader={headerMode === HeaderModeType.MINI}
        >
            {link ? (
                <BannerLink
                    to={link}
                    onClick={() => ga.oneHeader.trackHeaderBarActions(TrackHeaderBarBannerActions.Click)}
                >
                    <BannerText mobile={mobileHTML} desktop={desktopHTML} textColor={fontColor} />
                </BannerLink>
            ) : (
                <BannerText mobile={mobileHTML} desktop={desktopHTML} textColor={fontColor} />
            )}
            {dismissible && <StyledBannerDismissIcon textColor={fontColor} onClick={handleDismissIconClick} />}
        </StyledWrapper>
    );
};

export default HeaderBarBannerV2;
